import * as React from "react";

/**
 * Single localStorage document for detail-page layout chrome.
 *
 * Key: `lion.ui.detailLayout:v1`
 * Shape:
 * {
 *   navCollapsed?: boolean,
 *   sections?: { "<scope>": ["collapsed-section-id", ...] },
 *   sectionOrder?: { "<scope>": ["section-id", ...] }
 * }
 *
 * Only collapsed section IDs are stored (expanded is the default).
 * Empty scopes / empty documents are removed so storage stays quiet.
 */

const STORAGE_KEY = "lion.ui.detailLayout:v1";
const LEGACY_SECTIONS_KEY = "lion.ui.sectionCollapse";
const LEGACY_NAV_KEY = "lion.ui.sectionNavCollapsed";

export type DetailLayoutStore = {
  navCollapsed?: boolean;
  sections?: Record<string, string[]>;
  sectionOrder?: Record<string, string[]>;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cleanSectionIds(ids: unknown): string[] {
  if (!Array.isArray(ids)) return [];
  return ids.filter((id): id is string => typeof id === "string" && id.length > 0);
}

function normalizeStore(raw: unknown): DetailLayoutStore {
  if (!isPlainObject(raw)) return {};
  const store: DetailLayoutStore = {};

  if (typeof raw.navCollapsed === "boolean") {
    store.navCollapsed = raw.navCollapsed;
  }

  if (isPlainObject(raw.sections)) {
    const sections: Record<string, string[]> = {};
    for (const [scope, ids] of Object.entries(raw.sections)) {
      const cleaned = cleanSectionIds(ids);
      if (cleaned.length > 0) sections[scope] = cleaned;
    }
    if (Object.keys(sections).length > 0) store.sections = sections;
  }

  if (isPlainObject(raw.sectionOrder)) {
    const sectionOrder: Record<string, string[]> = {};
    for (const [scope, ids] of Object.entries(raw.sectionOrder)) {
      const cleaned = cleanSectionIds(ids);
      if (cleaned.length > 0) sectionOrder[scope] = cleaned;
    }
    if (Object.keys(sectionOrder).length > 0) store.sectionOrder = sectionOrder;
  }

  return store;
}

function isStoreEmpty(store: DetailLayoutStore): boolean {
  return (
    !store.navCollapsed &&
    (!store.sections || Object.keys(store.sections).length === 0) &&
    (!store.sectionOrder || Object.keys(store.sectionOrder).length === 0)
  );
}

function migrateLegacyInto(store: DetailLayoutStore): DetailLayoutStore {
  if (typeof window === "undefined") return store;
  const next = {
    ...store,
    sections: store.sections ? { ...store.sections } : undefined,
    sectionOrder: store.sectionOrder ? { ...store.sectionOrder } : undefined,
  };

  try {
    const legacySectionsRaw = localStorage.getItem(LEGACY_SECTIONS_KEY);
    if (legacySectionsRaw) {
      const parsed = JSON.parse(legacySectionsRaw) as unknown;
      if (isPlainObject(parsed)) {
        const sections = { ...(next.sections ?? {}) };
        for (const [scope, ids] of Object.entries(parsed)) {
          const cleaned = cleanSectionIds(ids);
          if (cleaned.length === 0) continue;
          // Prefer already-migrated / current values for a scope.
          if (!sections[scope]) sections[scope] = cleaned;
        }
        if (Object.keys(sections).length > 0) next.sections = sections;
      }
      localStorage.removeItem(LEGACY_SECTIONS_KEY);
    }
  } catch {
    try {
      localStorage.removeItem(LEGACY_SECTIONS_KEY);
    } catch {
      /* ignore */
    }
  }

  try {
    const legacyNav = localStorage.getItem(LEGACY_NAV_KEY);
    if (legacyNav != null) {
      if (next.navCollapsed === undefined) {
        next.navCollapsed = legacyNav === "1";
      }
      // Only persist true; false is the default and should not occupy storage.
      if (next.navCollapsed === false) delete next.navCollapsed;
      localStorage.removeItem(LEGACY_NAV_KEY);
    }
  } catch {
    try {
      localStorage.removeItem(LEGACY_NAV_KEY);
    } catch {
      /* ignore */
    }
  }

  return normalizeStore(next);
}

function readStore(): DetailLayoutStore {
  if (typeof window === "undefined") return {};
  try {
    const hasLegacy =
      localStorage.getItem(LEGACY_SECTIONS_KEY) != null ||
      localStorage.getItem(LEGACY_NAV_KEY) != null;

    const raw = localStorage.getItem(STORAGE_KEY);
    let store = raw ? normalizeStore(JSON.parse(raw) as unknown) : {};

    if (hasLegacy) {
      store = migrateLegacyInto(store);
      writeStore(store);
    }

    return store;
  } catch {
    return {};
  }
}

function writeStore(store: DetailLayoutStore): void {
  if (typeof window === "undefined") return;
  try {
    const normalized = normalizeStore(store);
    if (isStoreEmpty(normalized)) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  } catch {
    // Quota / private mode — ignore; UI still works in-memory.
  }
}

function readScope(scope: string): string[] {
  return readStore().sections?.[scope] ?? [];
}

function writeScope(scope: string, collapsedIds: string[]): void {
  const store = readStore();
  const sections = { ...(store.sections ?? {}) };
  const cleaned = cleanSectionIds(collapsedIds);
  if (cleaned.length === 0) delete sections[scope];
  else sections[scope] = cleaned;

  const next: DetailLayoutStore = { ...store };
  if (Object.keys(sections).length === 0) delete next.sections;
  else next.sections = sections;
  writeStore(next);
}

function readOrderScope(scope: string): string[] {
  return readStore().sectionOrder?.[scope] ?? [];
}

function writeOrderScope(scope: string, orderedIds: string[]): void {
  const store = readStore();
  const sectionOrder = { ...(store.sectionOrder ?? {}) };
  const cleaned = cleanSectionIds(orderedIds);
  if (cleaned.length === 0) delete sectionOrder[scope];
  else sectionOrder[scope] = cleaned;

  const next: DetailLayoutStore = { ...store };
  if (Object.keys(sectionOrder).length === 0) delete next.sectionOrder;
  else next.sectionOrder = sectionOrder;
  writeStore(next);
}

/**
 * Merge persisted order with currently available ids. Ids missing from the
 * persisted order (e.g. a newly added section) are placed right after their
 * predecessor in the default order, or first when they lead it.
 */
export function mergeSectionOrder(
  persisted: string[],
  availableIds: string[],
): string[] {
  const available = new Set(availableIds);
  const ordered: string[] = [];
  for (const id of persisted) {
    if (available.has(id)) {
      ordered.push(id);
      available.delete(id);
    }
  }
  availableIds.forEach((id, index) => {
    if (!available.has(id)) return;
    const prev = availableIds[index - 1];
    const at = prev === undefined ? 0 : ordered.indexOf(prev) + 1;
    ordered.splice(at, 0, id);
  });
  return ordered;
}

export function readNavCollapsed(): boolean {
  return readStore().navCollapsed === true;
}

export function writeNavCollapsed(collapsed: boolean): void {
  const store = readStore();
  const next: DetailLayoutStore = { ...store };
  if (collapsed) next.navCollapsed = true;
  else delete next.navCollapsed;
  writeStore(next);
}

/** Product settings: collapsed sections for one product (+ optional shadow). */
export function productSettingsCollapseScope(
  productId: string,
  shadowName?: string | null,
): string {
  return shadowName ? `ps:${productId}:${shadowName}` : `ps:${productId}`;
}

/** Localization definitions page sections (contexts). */
export const LOCALIZATION_DEFINITIONS_COLLAPSE_SCOPE = "ld";

export type SectionLayoutBindProps = {
  sections: { id: string; label: string; icon?: React.ReactNode }[];
  onSectionReorder: (orderedIds: string[]) => void;
  onExpandAllSections: () => void;
  onCollapseAllSections: () => void;
  areAllSectionsExpanded: boolean;
};

export type UseSectionCollapseResult = {
  isExpanded: (sectionId: string) => boolean;
  toggle: (sectionId: string) => void;
  expandAll: (ids: string[]) => void;
  collapseAll: (ids: string[]) => void;
  areAllExpanded: (ids: string[]) => boolean;
  areAllCollapsed: (ids: string[]) => boolean;
  sortByOrder: <T extends { id: string }>(items: T[]) => T[];
  reorder: (nextIds: string[]) => void;
  /** Ordered sections + DetailPageLayout bind props for reorder / expand-all. */
  bindSectionLayout: <T extends { id: string; label: string; icon?: React.ReactNode }>(
    items: T[],
  ) => SectionLayoutBindProps & { sections: T[] };
};

/**
 * Persist section collapse + order within a scope.
 * Expanded is the default; only collapsed IDs are stored.
 */
export function useSectionCollapse(scope: string): UseSectionCollapseResult {
  const [collapsed, setCollapsed] = React.useState(() => new Set(readScope(scope)));
  const [order, setOrder] = React.useState(() => readOrderScope(scope));

  React.useEffect(() => {
    setCollapsed(new Set(readScope(scope)));
    setOrder(readOrderScope(scope));
  }, [scope]);

  const isExpanded = React.useCallback(
    (sectionId: string) => !collapsed.has(sectionId),
    [collapsed],
  );

  const toggle = React.useCallback(
    (sectionId: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(sectionId)) next.delete(sectionId);
        else next.add(sectionId);
        writeScope(scope, [...next]);
        return next;
      });
    },
    [scope],
  );

  const expandAll = React.useCallback(
    (ids: string[]) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        writeScope(scope, [...next]);
        return next;
      });
    },
    [scope],
  );

  const collapseAll = React.useCallback(
    (ids: string[]) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.add(id);
        writeScope(scope, [...next]);
        return next;
      });
    },
    [scope],
  );

  const areAllExpanded = React.useCallback(
    (ids: string[]) => ids.length > 0 && ids.every((id) => !collapsed.has(id)),
    [collapsed],
  );

  const areAllCollapsed = React.useCallback(
    (ids: string[]) => ids.length > 0 && ids.every((id) => collapsed.has(id)),
    [collapsed],
  );

  const sortByOrder = React.useCallback(
    <T extends { id: string }>(items: T[]): T[] => {
      const availableIds = items.map((item) => item.id);
      const merged = mergeSectionOrder(order, availableIds);
      const byId = new Map(items.map((item) => [item.id, item]));
      return merged.map((id) => byId.get(id)!).filter(Boolean);
    },
    [order],
  );

  const reorder = React.useCallback(
    (nextIds: string[]) => {
      const cleaned = cleanSectionIds(nextIds);
      setOrder(cleaned);
      writeOrderScope(scope, cleaned);
    },
    [scope],
  );

  const bindSectionLayout = React.useCallback(
    <T extends { id: string; label: string; icon?: React.ReactNode }>(items: T[]) => {
      const ordered = sortByOrder(items);
      const ids = ordered.map((item) => item.id);
      return {
        sections: ordered,
        onSectionReorder: reorder,
        onExpandAllSections: () => expandAll(ids),
        onCollapseAllSections: () => collapseAll(ids),
        areAllSectionsExpanded: areAllExpanded(ids),
      };
    },
    [sortByOrder, reorder, expandAll, collapseAll, areAllExpanded],
  );

  return {
    isExpanded,
    toggle,
    expandAll,
    collapseAll,
    areAllExpanded,
    areAllCollapsed,
    sortByOrder,
    reorder,
    bindSectionLayout,
  };
}

/** Alias matching the broader layout concerns of this hook. */
export const useDetailLayout = useSectionCollapse;
